import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api, { BACKEND_URL } from '@/utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Receipt, Plus, Upload, Trash2, Loader2, DollarSign, FileText, CheckCircle2, XCircle, AlertCircle } from 'lucide-react';

const STATUS_STYLES = {
  pending: 'bg-amber-100 text-amber-800 border-amber-200',
  approved: 'bg-green-100 text-green-800 border-green-200',
  rejected: 'bg-red-100 text-red-800 border-red-200',
};

export default function ExpensesPage() {
  const { user } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [receiptFile, setReceiptFile] = useState(null);
  const [form, setForm] = useState({ item_description: '', amount: '', tax: '', notes: '' });

  const fetchExpenses = useCallback(async () => {
    try {
      const res = await api.get('/expenses');
      setExpenses(res.data);
    } catch (e) { process.env.NODE_ENV === 'development' && console.error(e); } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchExpenses(); }, [fetchExpenses]);

  const handleSubmit = async () => {
    if (!form.item_description.trim()) { toast.error('Item description is required'); return; }
    if (!form.amount || parseFloat(form.amount) <= 0) { toast.error('Valid amount is required'); return; }

    setCreating(true);
    try {
      const res = await api.post('/expenses', {
        item_description: form.item_description,
        amount: parseFloat(form.amount),
        tax: parseFloat(form.tax || 0),
        notes: form.notes,
      });
      const expenseId = res.data.expense_id;

      // Upload receipt if provided
      if (receiptFile) {
        const fd = new FormData();
        fd.append('file', receiptFile);
        await api.post(`/expenses/${expenseId}/receipt`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }

      toast.success('Expense submitted for approval!');
      setShowCreate(false);
      setForm({ item_description: '', amount: '', tax: '', notes: '' });
      setReceiptFile(null);
      fetchExpenses();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to submit expense');
    } finally { setCreating(false); }
  };

  const handleDelete = async (expenseId) => {
    if (!window.confirm('Delete this expense?')) return;
    try {
      await api.delete(`/expenses/${expenseId}`);
      toast.success('Expense deleted');
      setExpenses(prev => prev.filter(e => e.expense_id !== expenseId));
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to delete'); }
  };

  const pending = expenses.filter(e => e.status === 'pending');
  const approved = expenses.filter(e => e.status === 'approved');
  const rejected = expenses.filter(e => e.status === 'rejected');
  const approvedTotal = approved.reduce((sum, e) => sum + (e.total || 0), 0);

  if (loading) return <div className="flex items-center justify-center p-12"><img src="/logo.png" alt="Loading" className="w-12 h-12 animate-pulse" /></div>;

  return (
    <div className="max-w-4xl mx-auto animate-fadeIn" data-testid="expenses-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-800" style={{ fontFamily: 'Nunito' }}>Expenses</h1>
          <p className="text-slate-500 mt-1">Submit equipment expenses and upload receipts</p>
        </div>
        <Button data-testid="new-expense-btn" onClick={() => setShowCreate(true)} className="rounded-full bg-[#1E1B4B] hover:bg-[#2D2B66] text-white font-bold gap-2">
          <Plus className="w-4 h-4" /> New Expense
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        <Card className="rounded-2xl border-amber-100">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-extrabold text-amber-600">{pending.length}</p>
            <p className="text-xs text-slate-500 font-semibold">Pending</p>
          </CardContent>
        </Card>
        <Card className="rounded-2xl border-green-100">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-extrabold text-green-600">{approved.length}</p>
            <p className="text-xs text-slate-500 font-semibold">Approved</p>
          </CardContent>
        </Card>
        <Card className="rounded-2xl border-green-100">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-extrabold text-green-700">${approvedTotal.toFixed(2)}</p>
            <p className="text-xs text-slate-500 font-semibold">Total Approved</p>
          </CardContent>
        </Card>
      </div>

      {/* $150 Expense Threshold Warning */}
      {approvedTotal >= 112.50 && approvedTotal < 150 && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-6 flex items-start gap-3" data-testid="expense-threshold-warning">
          <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <p className="font-bold text-amber-800 text-sm">Approaching $150 expense limit</p>
            <p className="text-xs text-amber-600 mt-1">You've spent ${approvedTotal.toFixed(2)} of your $150 expense allowance. For your next purchase, please request funding through the Hours page instead.</p>
          </div>
        </div>
      )}
      {approvedTotal >= 150 && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-4 mb-6 flex items-start gap-3" data-testid="expense-over-limit">
          <AlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
          <div>
            <p className="font-bold text-red-800 text-sm">$150 expense limit reached</p>
            <p className="text-xs text-red-600 mt-1">You've spent ${approvedTotal.toFixed(2)}. Please request funding through the Hours page for any future purchases.</p>
          </div>
        </div>
      )}

      {/* Expense List */}
      {expenses.length === 0 ? (
        <Card className="rounded-3xl border-dashed border-2 border-slate-200">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Receipt className="w-16 h-16 text-slate-300 mb-4" />
            <p className="text-xl font-bold text-slate-400" style={{ fontFamily: 'Nunito' }}>No Expenses Yet</p>
            <p className="text-slate-400 mt-2 text-sm">Submit an expense for equipment or supplies</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="expenses-list">
          {expenses.map(expense => (
            <Card key={expense.expense_id} className="rounded-2xl border-slate-100 shadow-sm" data-testid={`expense-${expense.expense_id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <h3 className="font-bold text-slate-800 text-sm">{expense.item_description}</h3>
                      <Badge className={`rounded-full text-xs capitalize ${STATUS_STYLES[expense.status]}`}>
                        {expense.status === 'pending' && <AlertCircle className="w-3 h-3 mr-0.5" />}
                        {expense.status === 'approved' && <CheckCircle2 className="w-3 h-3 mr-0.5" />}
                        {expense.status === 'rejected' && <XCircle className="w-3 h-3 mr-0.5" />}
                        {expense.status}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-slate-600 mb-1">
                      <span>Amount: <strong>${expense.amount?.toFixed(2)}</strong></span>
                      <span>Tax: <strong>${expense.tax?.toFixed(2)}</strong></span>
                      <span className="font-bold text-slate-800">Total: ${expense.total?.toFixed(2)}</span>
                    </div>
                    {expense.notes && <p className="text-xs text-slate-500">{expense.notes}</p>}
                    {expense.admin_notes && (
                      <p className="text-xs text-purple-600 mt-1 bg-purple-50 rounded-lg px-2 py-1">Admin: {expense.admin_notes}</p>
                    )}
                    <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
                      <span>{new Date(expense.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                      {expense.receipt_filename && (
                        <a href={`${BACKEND_URL}/api/uploads/${expense.receipt_filename}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-[#0E7490] hover:underline">
                          <FileText className="w-3 h-3" /> View Receipt
                        </a>
                      )}
                    </div>
                  </div>
                  {expense.status === 'pending' && (
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400 hover:text-red-600 rounded-full shrink-0" onClick={() => handleDelete(expense.expense_id)} data-testid={`delete-expense-${expense.expense_id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Expense Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="rounded-3xl max-w-lg max-h-[90vh] max-h-[90dvh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Nunito' }} className="flex items-center gap-2">
              <Receipt className="w-5 h-5 text-[#22D3EE]" /> New Expense
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <label className="text-sm font-semibold text-slate-600">Item Description <span className="text-red-400">*</span></label>
              <Input
                value={form.item_description}
                onChange={e => setForm(f => ({ ...f, item_description: e.target.value }))}
                placeholder="e.g. Kickboard set, Pool noodles, Goggles..."
                className="rounded-xl mt-1"
                data-testid="expense-description"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-semibold text-slate-600">Amount ($) <span className="text-red-400">*</span></label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.amount}
                  onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
                  placeholder="0.00"
                  className="rounded-xl mt-1"
                  data-testid="expense-amount"
                />
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-600">Tax ($)</label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.tax}
                  onChange={e => setForm(f => ({ ...f, tax: e.target.value }))}
                  placeholder="0.00"
                  className="rounded-xl mt-1"
                  data-testid="expense-tax"
                />
              </div>
            </div>
            {form.amount && (
              <div className="bg-slate-50 rounded-xl p-3 text-center">
                <span className="text-sm text-slate-500">Total: </span>
                <span className="text-lg font-extrabold text-slate-800">${(parseFloat(form.amount || 0) + parseFloat(form.tax || 0)).toFixed(2)}</span>
              </div>
            )}
            <div>
              <label className="text-sm font-semibold text-slate-600">Notes (optional)</label>
              <Textarea
                value={form.notes}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Any additional details..."
                className="rounded-xl mt-1"
                rows={2}
                data-testid="expense-notes"
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-600">Receipt (photo or PDF)</label>
              <Input
                type="file"
                accept="image/*,.pdf"
                onChange={e => setReceiptFile(e.target.files?.[0] || null)}
                className="rounded-xl mt-1"
                data-testid="expense-receipt"
              />
              {receiptFile && <p className="text-xs text-slate-400 mt-1">{receiptFile.name}</p>}
            </div>
            <Button
              data-testid="submit-expense-btn"
              onClick={handleSubmit}
              disabled={creating}
              className="w-full rounded-full bg-[#1E1B4B] hover:bg-[#2D2B66] text-white font-bold h-11 gap-2"
            >
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Receipt className="w-4 h-4" />}
              {creating ? 'Submitting...' : 'Submit Expense'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
